import { useParams, Link } from "react-router-dom";
import { Phone, ArrowLeft, ChevronRight, CheckCircle, HelpCircle, Stethoscope } from "lucide-react";
import { specializationsData, treatmentsData } from "../data/specializations";

const doctorData = {
  name: "Dr. Prathyush V",
  phone: "+91 9901305665",
  photo: "https://drupal-cdn-hfaeddcdbng5hfbg.a01.azurefd.net/sites/default/files/2025-07/screenshot-2025-07-03-at-4.38.17-pm.png",
};

// Specialization Detail Page
export const SpecializationPage = () => {
  const { slug } = useParams();
  const data = specializationsData[slug];

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-['Outfit'] text-[#1A1D1A] mb-4">Page Not Found</h1>
          <Link to="/" className="text-[#2C5545] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7E2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="back-home">
            <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#1A1D1A] text-sm font-['Outfit']">Dr. Prathyush V</p>
              <p className="text-xs text-[#5C6B64]">Medical Oncologist</p>
            </div>
          </Link>
          <a
            href={`tel:${doctorData.phone}`}
            className="flex items-center gap-2 bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-2.5 rounded-full transition-all duration-300 text-sm font-medium"
            data-testid="header-call-btn"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Book Consultation</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2C5545] to-[#224235] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4A373] mb-4">
            Specialization
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-light mb-4" data-testid="page-title">
            {data.title}
          </h1>
          <p className="text-lg text-white/80 max-w-3xl">
            {data.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-[#5C6B64] text-lg leading-relaxed">
              {data.fullContent.intro}
            </p>
          </div>

          {/* Sections */}
          {data.fullContent.sections.map((section, idx) => (
            <div key={idx} className="mb-12">
              <h2 className="text-xl sm:text-2xl font-['Outfit'] font-medium text-[#1A1D1A] mb-4">
                {section.title}
              </h2>
              {section.content && (
                <p className="text-[#5C6B64] mb-4">{section.content}</p>
              )}
              {section.bullets && (
                <ul className="space-y-3 mb-6">
                  {section.bullets.map((bullet, bIdx) => (
                    <li key={bIdx} className="flex items-start gap-3 text-[#5C6B64]">
                      <CheckCircle className="w-5 h-5 text-[#2C5545] mt-0.5 flex-shrink-0" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.treatments && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {section.treatments.map((treatment, tIdx) => (
                    <div key={tIdx} className="bg-white p-6 rounded-xl border border-[#E5E7E2] shadow-sm">
                      <h3 className="font-['Outfit'] font-medium text-[#2C5545] mb-2">{treatment.name}</h3>
                      <p className="text-sm text-[#5C6B64]">{treatment.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* FAQs */}
          {data.fullContent.faqs && data.fullContent.faqs.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl sm:text-2xl font-['Outfit'] font-medium text-[#1A1D1A] mb-6 flex items-center gap-3">
                <HelpCircle className="w-6 h-6 text-[#D4A373]" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {data.fullContent.faqs.map((faq, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl border border-[#E5E7E2]">
                    <h3 className="font-['Outfit'] font-medium text-[#1A1D1A] mb-2">{faq.q}</h3>
                    <p className="text-[#5C6B64]">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-16 bg-[#2C5545] rounded-2xl p-8 text-center">
            <h3 className="text-xl font-['Outfit'] text-white mb-4">
              Need Expert Consultation?
            </h3>
            <p className="text-white/80 mb-6">
              Consult Dr. Prathyush V for personalized cancer care
            </p>
            <a
              href={`tel:${doctorData.phone}`}
              className="inline-flex items-center gap-2 bg-white text-[#2C5545] px-8 py-3 rounded-full font-medium hover:bg-[#F9F8F6] transition-all"
              data-testid="cta-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call {doctorData.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1D1A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Dr. Prathyush V - Apollo Hospitals, Bangalore
          </p>
        </div>
      </footer>
    </div>
  );
};

// Treatment Detail Page
export const TreatmentPage = () => {
  const { slug } = useParams();
  const data = treatmentsData[slug];

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-['Outfit'] text-[#1A1D1A] mb-4">Page Not Found</h1>
          <Link to="/" className="text-[#2C5545] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7E2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#1A1D1A] text-sm font-['Outfit']">Dr. Prathyush V</p>
              <p className="text-xs text-[#5C6B64]">Medical Oncologist</p>
            </div>
          </Link>
          <a
            href={`tel:${doctorData.phone}`}
            className="flex items-center gap-2 bg-[#2C5545] hover:bg-[#224235] text-white px-5 py-2.5 rounded-full transition-all duration-300 text-sm font-medium"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden sm:inline">Book Consultation</span>
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#D4A373] to-[#c4935f] text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/80 mb-4">
            Treatment & Procedure
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-light mb-4">
            {data.title}
          </h1>
          <p className="text-lg text-white/90 max-w-3xl">
            {data.shortDescription}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-[#5C6B64] text-lg leading-relaxed">
              {data.fullContent.intro}
            </p>
          </div>

          {data.fullContent.sections.map((section, idx) => (
            <div key={idx} className="mb-12">
              <h2 className="text-xl sm:text-2xl font-['Outfit'] font-medium text-[#1A1D1A] mb-4">
                {section.title}
              </h2>
              {section.content && (
                <p className="text-[#5C6B64] mb-4">{section.content}</p>
              )}
              {section.bullets && (
                <ul className="space-y-3">
                  {section.bullets.map((bullet, bIdx) => (
                    <li key={bIdx} className="flex items-start gap-3 text-[#5C6B64]">
                      <ChevronRight className="w-5 h-5 text-[#D4A373] mt-0.5 flex-shrink-0" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}

          {/* CTA */}
          <div className="mt-16 bg-[#2C5545] rounded-2xl p-8 text-center">
            <h3 className="text-xl font-['Outfit'] text-white mb-4">
              Learn More About Your Treatment Options
            </h3>
            <p className="text-white/80 mb-6">
              Consult Dr. Prathyush V for expert guidance
            </p>
            <a
              href={`tel:${doctorData.phone}`}
              className="inline-flex items-center gap-2 bg-white text-[#2C5545] px-8 py-3 rounded-full font-medium hover:bg-[#F9F8F6] transition-all"
            >
              <Phone className="w-5 h-5" />
              Call {doctorData.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1D1A] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/60 text-sm">
            © {new Date().getFullYear()} Dr. Prathyush V - Apollo Hospitals, Bangalore
          </p>
        </div>
      </footer>
    </div>
  );
};

export default { SpecializationPage, TreatmentPage };
